import {
  AlertCircle,
  ArrowUpDown,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Edit2,
  FileText,
  GitBranch,
  HelpCircle,
  List,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch as fetch } from '../services/api';

interface Question {
  id: string;
  lesson_id: string;
  type: string;
  text?: string;  // From DB
  question_text?: string;  // Computed
  correct_answer?: string;
  explanation?: string;
  options?: string[] | Array<{ id: string; text: string }>;
  matching_pairs?: Array<{ left: string; right: string }>;
  order_items?: Array<{ id: string; text: string; correctOrder: number }>;
  sequence?: unknown[];
  sequence_answer?: unknown[];
  difficulty: string;
  order: number;
  hint?: string;
  // FillBlank specific
  blank_sentence?: string;
  blank_options?: string[];
  blank_correct?: string;
  // Correction specific
  incorrect_sentence?: string;
  corrected_sentence?: string;
}

interface Props {
  lessonId: string;
  lessonName: string;
  onClose: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Type icons
const typeIcons: Record<string, React.ReactNode> = {
  fillBlank: <FileText className="w-4 h-4" />,
  mcq: <CheckSquare className="w-4 h-4" />,
  correction: <Edit2 className="w-4 h-4" />,
  matching: <GitBranch className="w-4 h-4" />,
  ordering: <ArrowUpDown className="w-4 h-4" />,
  sequence: <List className="w-4 h-4" />,
};

const typeNames: Record<string, string> = {
  fillBlank: 'Texte à trous',
  mcq: 'QCM',
  correction: 'Correction',
  matching: 'Association',
  ordering: 'Ordre',
  sequence: 'Séquence',
};

const typeColors: Record<string, string> = {
  fillBlank: 'bg-blue-100 text-blue-700',
  mcq: 'bg-green-100 text-green-700',
  correction: 'bg-purple-100 text-purple-700',
  matching: 'bg-amber-100 text-amber-700',
  ordering: 'bg-pink-100 text-pink-700',
  sequence: 'bg-cyan-100 text-cyan-700',
};

const QuestionManager: React.FC<Props> = ({ lessonId, lessonName, onClose }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadQuestions();
  }, [lessonId]);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/learning/admin/lessons/${lessonId}/questions`);
      if (!response.ok) throw new Error('Erreur de chargement');
      const data = await response.json();
      setQuestions(data.questions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (questionId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/learning/admin/questions/${questionId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Erreur de suppression');
      setQuestions(questions.filter(q => q.id !== questionId));
      setShowDeleteConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedQuestion(expandedQuestion === id ? null : id);
  };

  // Helper to get display text
  const getQuestionText = (question: Question): string => {
    return question.blank_sentence || question.incorrect_sentence || question.text || '';
  };

  const renderQuestionContent = (question: Question) => {
    switch (question.type) {
      case 'fillBlank':
        return (
          <div className="space-y-2">
            <div>
              <span className="text-xs font-medium text-gray-500">Phrase à trous:</span>
              <p className="text-sm bg-gray-50 p-2 rounded">{question.blank_sentence || question.text}</p>
            </div>
            {question.blank_options && (
              <div>
                <span className="text-xs font-medium text-gray-500">Options:</span>
                <div className="flex gap-2 mt-1">
                  {question.blank_options.map((opt, idx) => (
                    <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm">{opt}</span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <span className="text-xs font-medium text-gray-500">Réponse:</span>
              <p className="text-sm bg-green-50 text-green-700 p-2 rounded font-medium">
                {question.blank_correct || question.correct_answer}
              </p>
            </div>
          </div>
        );

      case 'mcq':
        return (
          <div className="space-y-2">
            <div>
              <span className="text-xs font-medium text-gray-500">Question:</span>
              <p className="text-sm bg-gray-50 p-2 rounded">{question.text}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500">Options:</span>
              <div className="space-y-1 mt-1">
                {Array.isArray(question.options) && question.options.map((opt, idx) => {
                  const optText = typeof opt === 'string' ? opt : opt.text;
                  const optId = typeof opt === 'string' ? String.fromCharCode(65 + idx) : opt.id;
                  const isCorrect = optText === question.correct_answer || optId === question.correct_answer;
                  return (
                    <div
                      key={idx}
                      className={`text-sm p-2 rounded flex items-center gap-2 ${isCorrect ? 'bg-green-50 text-green-700 font-medium' : 'bg-gray-50'
                        }`}
                    >
                      <span className="font-mono text-xs bg-gray-200 px-1.5 py-0.5 rounded">{optId}</span>
                      {optText}
                      {isCorrect && <span className="ml-auto text-xs">✓ Correct</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'correction':
        return (
          <div className="space-y-2">
            <div>
              <span className="text-xs font-medium text-gray-500">Phrase incorrecte:</span>
              <p className="text-sm bg-red-50 text-red-700 p-2 rounded line-through">
                {question.incorrect_sentence || question.text}
              </p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500">Correction:</span>
              <p className="text-sm bg-green-50 text-green-700 p-2 rounded">
                {question.corrected_sentence || question.correct_answer}
              </p>
            </div>
          </div>
        );

      case 'matching':
        return (
          <div className="space-y-2">
            <span className="text-xs font-medium text-gray-500">Paires à associer:</span>
            <div className="grid grid-cols-2 gap-2">
              <div className="text-xs font-medium text-gray-400 uppercase">Gauche</div>
              <div className="text-xs font-medium text-gray-400 uppercase">Droite</div>
              {question.matching_pairs?.map((pair, idx) => (
                <React.Fragment key={idx}>
                  <div className="text-sm bg-blue-50 p-2 rounded">{pair.left}</div>
                  <div className="text-sm bg-indigo-50 p-2 rounded">{pair.right}</div>
                </React.Fragment>
              ))}
            </div>
          </div>
        );

      case 'ordering':
        return (
          <div className="space-y-2">
            <span className="text-xs font-medium text-gray-500">Éléments à ordonner:</span>
            <div className="space-y-1">
              {question.order_items
                ?.sort((a, b) => a.correctOrder - b.correctOrder)
                .map((item, idx) => (
                  <div key={idx} className="text-sm bg-gray-50 p-2 rounded flex items-center gap-2">
                    <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-bold">
                      {item.correctOrder}
                    </span>
                    {item.text}
                  </div>
                ))}
            </div>
          </div>
        );

      case 'sequence':
        return (
          <div className="space-y-2">
            <div>
              <span className="text-xs font-medium text-gray-500">Séquence à compléter:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {(question.sequence as string[])?.map((item, idx) => (
                  <span
                    key={idx}
                    className={`px-3 py-1 rounded text-sm ${item === '?' ? 'bg-amber-100 text-amber-700 font-bold' : 'bg-gray-100'
                      }`}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500">Réponse:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {(question.sequence_answer as string[])?.map((item, idx) => (
                  <span key={idx} className="px-3 py-1 rounded text-sm bg-green-100 text-green-700">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-sm text-gray-500">
            Type non supporté: {question.type}
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <HelpCircle className="w-5 h-5" />
              Questions - {lessonName}
            </h2>
            <p className="text-sm text-white/80">{questions.length} exercices</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadQuestions}
              className="p-2 hover:bg-white/20 rounded-lg transition"
              title="Actualiser"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune question dans cette leçon</p>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map((question, idx) => (
                <div
                  key={question.id}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Question header */}
                  <div
                    className="p-3 bg-gray-50 flex items-center gap-3 cursor-pointer hover:bg-gray-100 transition"
                    onClick={() => toggleExpand(question.id)}
                  >
                    <span className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </span>
                    <div
                      className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${typeColors[question.type] || 'bg-gray-100 text-gray-700'
                        }`}
                    >
                      {typeIcons[question.type] || <HelpCircle className="w-4 h-4" />}
                      {typeNames[question.type] || question.type}
                    </div>
                    <span className="flex-1 text-sm text-gray-700 truncate">
                      {getQuestionText(question).substring(0, 60)}...
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteConfirm(question.id);
                        }}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expandedQuestion === question.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded content */}
                  {expandedQuestion === question.id && (
                    <div className="p-4 border-t border-gray-200 space-y-4">
                      {renderQuestionContent(question)}

                      {/* Explanation */}
                      {question.explanation && (
                        <div>
                          <span className="text-xs font-medium text-gray-500">Explication:</span>
                          <p className="text-sm bg-blue-50 text-blue-700 p-2 rounded mt-1">
                            {question.explanation}
                          </p>
                        </div>
                      )}

                      {/* Hint */}
                      {question.hint && (
                        <div>
                          <span className="text-xs font-medium text-gray-500">Indice:</span>
                          <p className="text-sm bg-amber-50 text-amber-700 p-2 rounded mt-1">
                            {question.hint}
                          </p>
                        </div>
                      )}

                      {/* Meta info */}
                      <div className="flex items-center gap-4 text-xs text-gray-400 pt-2 border-t">
                        <span>Difficulté: {question.difficulty}</span>
                        <span>Ordre: {question.order}</span>
                        <span className="font-mono">{question.id.substring(0, 8)}...</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 max-w-sm mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Supprimer la question?</h3>
                  <p className="text-sm text-gray-500">Cette action est irréversible</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 bg-gray-200 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionManager;
